import { getWithAuth, postWithAuth } from "./InitializeService";
import { assertNotImpersonating } from "../Utility/devImpersonation";

/**
 * Payroll Service (Spoke)
 * Domain-specific service methods for the Pay Stubs / W2 self-service module
 * All endpoints use PBS Enterprise API base URL: https://data-core.teampbs.com/enterprise/api
 *
 * Every endpoint here is implicitly scoped to the caller's own bearer token - none of them
 * accept a parameter naming another person, so this spoke can only request or list the
 * signed-in employee's own pay stubs and W2s.
 */

export interface CheckDatesResponse {
  checkDates: string[];
}

export interface CheckNumbersResponse {
  checkDate: string;
  checkNumbers: string[];
}

export type TaxDocumentRequest =
  | { documentType: "W2"; taxYear: number }
  | { documentType: "Paystub"; checkDate: string; checkNumber: string };

export interface TaxDocumentFile {
  blob: Blob;
  fileName: string;
}

/**
 * Get the dates the current user was paid on
 * An empty list is a normal success (e.g. the user has never been paid), not an error.
 * @returns Promise with { checkDates: string[] } (YYYY-MM-DD, most recent first)
 */
export const getPayStubCheckDates = async () => {
  assertNotImpersonating("Pay stubs");
  return getWithAuth("payroll/paystubs/checkdates", {});
};

/**
 * Get the check number(s) for one of the current user's own check dates
 * An empty list is a normal success (e.g. a date the user wasn't paid on), not an error.
 * @param checkDate - Check date in YYYY-MM-DD format
 * @returns Promise with { checkDate: string, checkNumbers: string[] }
 */
export const getPayStubCheckNumbers = async (checkDate: string) => {
  assertNotImpersonating("Pay stubs");
  return getWithAuth("payroll/paystubs/checknumbers", {
    params: { checkDate },
  });
};

/**
 * Extract the suggested file name from a Content-Disposition header
 * e.g. `attachment; filename="W2-2025.pdf"` -> "W2-2025.pdf"
 * @param contentDisposition - Raw Content-Disposition response header value
 * @param fallback - File name to use when the header is missing or unparsable
 */
const parseFileName = (
  contentDisposition: string | undefined,
  fallback: string,
): string => {
  if (!contentDisposition) return fallback;

  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
    contentDisposition,
  );
  if (!match) return fallback;

  try {
    return decodeURIComponent(match[1]);
  } catch (error) {
    return match[1];
  }
};

/**
 * Recover a human-readable error message from a failed request
 * When the request is made with responseType: "blob", a JSON error body still arrives
 * as a Blob, not parsed JSON - this reads it as text and attempts JSON.parse so the
 * caller never has to special-case a blob-shaped error.
 * @param error - Error thrown by axios
 * @param fallback - Message to use when nothing better can be recovered
 */
const extractErrorMessage = async (
  error: any,
  fallback: string,
): Promise<string> => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return (
          parsed?.message || parsed?.title || parsed?.error || text || fallback
        );
      } catch {
        return text || fallback;
      }
    } catch {
      return fallback;
    }
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.title ||
    error?.message ||
    fallback
  );
};

/**
 * Request a tax document (W2 or Paystub) as a downloadable PDF for the current user
 * @param payload - W2 (documentType + taxYear) or Paystub (documentType + checkDate + checkNumber) request
 * @param fallbackFileName - Used when the server doesn't supply a name via Content-Disposition
 * @returns Promise resolving to the PDF blob and a file name to save it as
 */
export const requestTaxDocument = async (
  payload: TaxDocumentRequest,
  fallbackFileName: string,
): Promise<TaxDocumentFile> => {
  assertNotImpersonating("Tax documents");

  try {
    const response = await postWithAuth("payroll/taxdocuments", payload, {
      responseType: "blob",
    });

    return {
      blob: response.data as Blob,
      fileName: parseFileName(
        response.headers?.["content-disposition"],
        fallbackFileName,
      ),
    };
  } catch (error: unknown) {
    const message = await extractErrorMessage(
      error,
      "Failed to retrieve the requested document. Please try again.",
    );
    throw new Error(message);
  }
};
